import React, { useState } from 'react';
import OrganizerLayout from '../../components/layouts/OrganizerLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../components/ui/accordion';
import { 
  HelpCircle, Search, BookOpen, PlayCircle, MessageSquare, 
  Users, CreditCard, Calendar, FileText, Building2, Megaphone,
  UserCheck, ClipboardList, Upload, Download, Mail, ChevronRight,
  Lightbulb, ExternalLink, Phone
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Help = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  // Quick Start Tutorials
  const tutorials = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      description: 'Learn the basics of setting up your reunion',
      icon: BookOpen,
      color: 'bg-blue-500',
      steps: [
        'Create your event with school name and date',
        'Share your unique event code with batchmates',
        'They register using the code to join your reunion'
      ]
    },
    {
      id: 'managing-attendees',
      title: 'Managing Attendees',
      description: 'Add, import, and organize your attendees',
      icon: Users,
      color: 'bg-green-500',
      steps: [
        'View all registered attendees in the Attendees page',
        'Add individual attendees manually with "Add Attendee"',
        'Bulk import multiple attendees using CSV upload'
      ]
    },
    {
      id: 'payments',
      title: 'Payment Tracking',
      description: 'Track contributions and verify payments',
      icon: CreditCard,
      color: 'bg-amber-500',
      steps: [
        'Attendees submit payment proofs via the app',
        'Verify or reject payments with reasons',
        'Manually record cash payments received'
      ]
    },
    {
      id: 'committees',
      title: 'Committee Setup',
      description: 'Organize your planning team',
      icon: Building2,
      color: 'bg-purple-500',
      steps: [
        'Create committees (Food, Program, Finance, etc.)',
        'Assign committee heads and members',
        'Committee members get special permissions'
      ]
    },
    {
      id: 'announcements',
      title: 'Making Announcements',
      description: 'Communicate with all attendees',
      icon: Megaphone,
      color: 'bg-red-500',
      steps: [
        'Create announcements from the Announcements page',
        'Mark important announcements for priority',
        'Track who has acknowledged each announcement'
      ]
    },
    {
      id: 'event-day',
      title: 'Event Day Check-in',
      description: 'Manage attendance on reunion day',
      icon: UserCheck,
      color: 'bg-teal-500',
      steps: [
        'Use the Check-in page on event day',
        'Scan QR codes or search by name',
        'Track real-time attendance statistics'
      ]
    }
  ];

  // FAQ Categories
  const faqCategories = [
    {
      category: 'Account & Registration',
      icon: Users,
      faqs: [
        {
          question: 'How do attendees join my reunion event?',
          answer: 'Share your unique event code (shown on your dashboard) with your batchmates. They go to batchm8s.com, click "Join Reunion", enter the code, and register with their email. Once registered, they can access the attendee portal.'
        },
        {
          question: 'Can I add attendees who cannot register themselves?',
          answer: 'Yes! Go to Attendees page and click "Add Attendee" to manually add someone. You\'ll receive a temporary password to share with them. For multiple attendees, use "Bulk Import" to upload a CSV file.'
        },
        {
          question: 'How do I reset an attendee\'s password?',
          answer: 'Currently, attendees can use the "Forgot Password" link on the login page. As an organizer, you can remove and re-add an attendee to generate a new temporary password.'
        }
      ]
    },
    {
      category: 'Payments & Finance',
      icon: CreditCard,
      faqs: [
        {
          question: 'How does payment tracking work?',
          answer: 'Attendees submit their payment proofs (GCash screenshots, bank transfer receipts) through the app. You review these in the Payments page and verify or reject them. You can also manually record cash payments.'
        },
        {
          question: 'Can I track different types of payments?',
          answer: 'Yes! The system supports Registration Fees, Contributions, and Event Fees. Each payment is tagged with a type and method (GCash, Bank Transfer, Cash).'
        },
        {
          question: 'How do I generate financial reports?',
          answer: 'Go to the Reports page, select the Payments tab, and export to Excel or PDF. This includes all payment details, status, and totals.'
        },
        {
          question: 'What if I need to reject a payment?',
          answer: 'Click "Reject" on any pending payment and provide a reason (e.g., "Amount mismatch", "Invalid reference number"). The attendee will see the rejection reason.'
        }
      ]
    },
    {
      category: 'Committees & Tasks',
      icon: Building2,
      faqs: [
        {
          question: 'What committees can I create?',
          answer: 'You can create any committee! Common ones include: Finance, Food & Catering, Program & Entertainment, Registration, Decorations, Documentation, and Transportation. Each can have a head and multiple members.'
        },
        {
          question: 'What special permissions do committee members have?',
          answer: 'Committee heads can manage their committee members and tasks. Finance committee members can view and verify payments. All committee members can report progress on assigned tasks.'
        },
        {
          question: 'How do I assign tasks to committee members?',
          answer: 'Go to the Tasks page, create a task, select the category/committee, and assign it to a specific member. They\'ll be notified and can submit progress reports.'
        }
      ]
    },
    {
      category: 'Announcements & Documents',
      icon: FileText,
      faqs: [
        {
          question: 'How do I make sure everyone sees important announcements?',
          answer: 'Mark announcements as "Important" when creating them - they\'ll appear highlighted. You can also require acknowledgment and track who has seen each announcement.'
        },
        {
          question: 'What file types can I upload to Documents?',
          answer: 'You can upload images (JPG, PNG), documents (PDF, DOC), and spreadsheets. There\'s a file size limit per document. Use Documents to share itineraries, photos, sponsor materials, etc.'
        },
        {
          question: 'Can attendees upload documents?',
          answer: 'Currently, only organizers and authorized committee members can upload documents. Attendees can view and download shared documents.'
        }
      ]
    },
    {
      category: 'Program & Schedule',
      icon: Calendar,
      faqs: [
        {
          question: 'How do I create the event program/schedule?',
          answer: 'Go to the Program page and add activities with start time, duration, and details. You can organize by categories like Registration, Meals, Games, Performances, etc.'
        },
        {
          question: 'Can I reorder program items?',
          answer: 'Yes, you can drag and drop program items to reorder them, or edit the start times to adjust the schedule.'
        }
      ]
    },
    {
      category: 'Event Day',
      icon: UserCheck,
      faqs: [
        {
          question: 'How does check-in work on event day?',
          answer: 'Open the Check-in page on your phone or tablet. You can either scan the QR code shown in each attendee\'s app, or search by name and manually check them in.'
        },
        {
          question: 'What if someone arrives who isn\'t registered?',
          answer: 'You can quickly add them using "Add Attendee" on the Attendees page, then check them in immediately. Consider having a committee member handle walk-in registrations.'
        },
        {
          question: 'Can I see real-time attendance statistics?',
          answer: 'Yes! The Check-in page shows live statistics - total registered, checked in, and not yet arrived. The Reports page provides detailed attendance data for export.'
        }
      ]
    },
    {
      category: 'Subscriptions & Billing',
      icon: CreditCard,
      faqs: [
        {
          question: 'What subscription plans are available?',
          answer: 'We offer: Basic (Free, limited features), Organized Reunion (₱3,999 for most batches), Full Transparency (₱7,999 for large events with audit trails), and Organizer License (₱19,999/year for unlimited events).'
        },
        {
          question: 'How do I upgrade my subscription?',
          answer: 'Click "Upgrade Reunion" in the sidebar or go to the Pricing page. Add your desired plan to cart, apply any promo codes, and complete payment via PayPal or manual bank transfer.'
        },
        {
          question: 'Do promo codes expire?',
          answer: 'Yes, promo codes have usage limits and may expire. Enter valid promo codes at checkout before completing payment.'
        }
      ]
    }
  ];

  // Filter FAQs based on search
  const filteredFaqs = searchQuery
    ? faqCategories.map(cat => ({
        ...cat,
        faqs: cat.faqs.filter(faq => 
          faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
          faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
        )
      })).filter(cat => cat.faqs.length > 0)
    : faqCategories;

  return (
    <OrganizerLayout>
      <div className="space-y-6 animate-fade-in pb-20" data-testid="help-page">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto">
          <div className="w-16 h-16 bg-maroon/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <HelpCircle className="h-8 w-8 text-maroon" />
          </div>
          <h1 className="font-heading text-3xl font-bold text-maroon">
            Help & FAQ
          </h1>
          <p className="text-gray-600 mt-2">
            Everything you need to organize a successful reunion
          </p>
        </div>

        {/* Search */}
        <div className="max-w-xl mx-auto">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search for help topics..."
              className="pl-10 py-6 text-lg bg-white border-gray-200"
              data-testid="help-search"
            />
          </div>
        </div>

        {/* Quick Tips Banner */}
        <Card className="bg-gradient-to-r from-maroon to-maroon-hover text-white border-0">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-white/20 rounded-lg">
                <Lightbulb className="h-6 w-6" />
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-2">Quick Tip</h3>
                <p className="text-white/90">
                  Share your event code <span className="font-mono bg-white/20 px-2 py-1 rounded">THS5031</span> with 
                  your batchmates via your class group chat. They can register at batchm8s.com and start confirming attendance!
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tutorials Section */}
        <div>
          <h2 className="font-heading text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <PlayCircle className="h-5 w-5 text-maroon" />
            Quick Start Tutorials
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {tutorials.map((tutorial) => (
              <Card 
                key={tutorial.id} 
                className="border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                data-testid={`tutorial-${tutorial.id}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${tutorial.color} text-white`}>
                      <tutorial.icon className="h-5 w-5" />
                    </div>
                    <div>
                      <CardTitle className="text-base">{tutorial.title}</CardTitle>
                      <CardDescription className="text-xs">{tutorial.description}</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <ol className="space-y-2">
                    {tutorial.steps.map((step, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center text-xs font-medium text-gray-500">
                          {idx + 1}
                        </span>
                        <span>{step}</span>
                      </li>
                    ))}
                  </ol>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* FAQ Section */}
        <div>
          <h2 className="font-heading text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-maroon" />
            Frequently Asked Questions
          </h2>
          
          {filteredFaqs.length === 0 ? (
            <Card className="border-0 shadow-sm">
              <CardContent className="py-12 text-center">
                <Search className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">No results found for "{searchQuery}"</p>
                <Button 
                  variant="link" 
                  className="text-maroon mt-2"
                  onClick={() => setSearchQuery('')}
                >
                  Clear search
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {filteredFaqs.map((category) => (
                <Card key={category.category} className="border-0 shadow-sm">
                  <CardHeader className="pb-0">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <category.icon className="h-5 w-5 text-maroon" />
                      {category.category}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <Accordion type="single" collapsible className="w-full">
                      {category.faqs.map((faq, idx) => (
                        <AccordionItem key={idx} value={`${category.category}-${idx}`}>
                          <AccordionTrigger className="text-left hover:no-underline">
                            <span className="font-medium text-gray-900">{faq.question}</span>
                          </AccordionTrigger>
                          <AccordionContent className="text-gray-600 leading-relaxed">
                            {faq.answer}
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Contact Support */}
        <Card className="border-0 shadow-sm bg-cream-paper">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-maroon/10 rounded-lg">
                  <MessageSquare className="h-6 w-6 text-maroon" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Still need help?</h3>
                  <p className="text-sm text-gray-600">Our support team is ready to assist you</p>
                </div>
              </div>
              <div className="flex gap-3">
                <Button 
                  variant="outline"
                  onClick={() => navigate('/organizer/support')}
                  className="gap-2"
                >
                  <Mail className="h-4 w-4" />
                  Submit Ticket
                </Button>
                <Button 
                  className="bg-maroon hover:bg-maroon-hover gap-2"
                  onClick={() => window.open('mailto:support@batchm8s.com', '_blank')}
                >
                  <Phone className="h-4 w-4" />
                  Contact Us
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Links */}
        <div className="grid md:grid-cols-4 gap-4">
          <Button 
            variant="outline" 
            className="h-auto py-4 flex flex-col items-center gap-2 hover:border-maroon hover:text-maroon"
            onClick={() => navigate('/organizer/attendees')}
          >
            <Users className="h-6 w-6" />
            <span className="text-sm">Manage Attendees</span>
          </Button>
          <Button 
            variant="outline" 
            className="h-auto py-4 flex flex-col items-center gap-2 hover:border-maroon hover:text-maroon"
            onClick={() => navigate('/organizer/payments')}
          >
            <CreditCard className="h-6 w-6" />
            <span className="text-sm">Track Payments</span>
          </Button>
          <Button 
            variant="outline" 
            className="h-auto py-4 flex flex-col items-center gap-2 hover:border-maroon hover:text-maroon"
            onClick={() => navigate('/organizer/reports')}
          >
            <Download className="h-6 w-6" />
            <span className="text-sm">Export Reports</span>
          </Button>
          <Button 
            variant="outline" 
            className="h-auto py-4 flex flex-col items-center gap-2 hover:border-maroon hover:text-maroon"
            onClick={() => navigate('/pricing')}
          >
            <ChevronRight className="h-6 w-6" />
            <span className="text-sm">View Pricing</span>
          </Button>
        </div>
      </div>
    </OrganizerLayout>
  );
};

export default Help;
